/**
 * A tiny async-state shape shared by every store.
 *
 * The console keeps server state in Zustand rather than a query library, so
 * this gives each slice the same loading/error/stale vocabulary without each
 * store reinventing it.
 */

export interface AsyncState<T> {
  data: T
  loading: boolean
  /** True only for the very first load, so lists can skeleton once. */
  initialised: boolean
  error: string | null
  fetchedAt: number | null
}

export function idle<T>(initial: T): AsyncState<T> {
  return { data: initial, loading: false, initialised: false, error: null, fetchedAt: null }
}

export function loading<T>(state: AsyncState<T>): AsyncState<T> {
  return { ...state, loading: true, error: null }
}

export function loaded<T>(data: T): AsyncState<T> {
  return { data, loading: false, initialised: true, error: null, fetchedAt: Date.now() }
}

export function failed<T>(state: AsyncState<T>, error: string): AsyncState<T> {
  return { ...state, loading: false, initialised: true, error }
}

/** Whether a slice is older than `maxAgeMs` and worth refetching. */
export function isStale(state: AsyncState<unknown>, maxAgeMs = 30_000): boolean {
  if (!state.fetchedAt) return true
  return Date.now() - state.fetchedAt > maxAgeMs
}
